/**
 * @fileoverview The gift page, grow.gardengameplan.com/gift: give someone a year of Grow
 * More. Public (no account needed), in English and Spanish.
 *
 * Before gifts are on sale (the Grow setting gift.ON_SALE_FROM, December 1)
 * it says when they open; on sale but not set up on the server (Stripe, the
 * gift price or the gift table missing) it says gifts are not available yet.
 * Open, it asks for:
 *   - the buyer's name and email (the receipt and the code go there);
 *   - the recipient's name and, optionally, email: without one the buyer
 *     gives the code themselves (print it, or send the link);
 *   - a short message;
 *   - with a recipient email, when the gift email goes (today, or a chosen
 *     day up to a year out, at the Grow setting's hour, 8 a.m., in the
 *     buyer's own time zone) and in which language. The page sends the day
 *     and the browser's time zone; the server works out the moment
 *     (architecture audit batch 8);
 * then sends them to Stripe Checkout for one payment of the gift price
 * ("$39, paid once. Nothing renews by itself."). Coming back from a
 * cancelled payment (?cancelled=1) it says nothing was charged.
 *
 * Payment is by card only (Stripe Checkout in payment mode, D14); the page
 * never sees the card.
 *
 * Where: the route /gift (App.tsx), public, outside the Layout, in GiftShell.
 * API: GET /gifts/shop (getGiftShop) on opening; POST /gifts/checkout with
 * the order (startGiftCheckout), then the browser leaves for Stripe.
 * Settings: the Grow setting gift.ON_SALE_FROM (the shop's state 'soon'
 * and opensOn); sendMaxDays (how far ahead the gift email may go);
 * limits.GIFT_MESSAGE_MAX (the server's, lib/growSettings); the hour a gift email goes is
 * the server's (gift.SEND_LOCAL_HOUR), which the shop answer sends (sendHour)
 * for gift.buy.whenHint's {hour}; ?lang=
 * and ?cancelled=1.
 * Words (en/es): gift.buy.*, sitewide.loading.
 * Watch out: a refusal for blocked words (D15) comes back as
 * response.data.problems ('messageWords', 'nameWords') and is said plainly;
 * the field checks here are only the obvious ones, the server checks again.
 * A failure on our side (a 500, no connection; D31, lib/serverFailure) says
 * the standard sentence, reported to Sentry, with the support address as a
 * link, in place of gift.buy.loadFailed and gift.buy.failed's "Please try
 * again" (lib/errorMessage).
 *
 * @module GiftPage
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { getGiftShop, startGiftCheckout, type GiftOrder, type GiftShop } from '@/shared/api/gifts';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { formatPrice } from '@/features/billing/lib/useBilling';
import { GiftShell } from './components/GiftShell';
import { useGiftLanguage } from './lib/useGiftLanguage';
import { usePageRestored } from '@/shared/hooks/usePageRestored';

/** The form's fields, as typed. */
interface Form {
  /** The buyer's name, for the gift email ("Amy gave you…"); required. */
  buyerName: string;
  /** Where the receipt and the code go; required. */
  buyerEmail: string;
  /** The recipient's name; optional. */
  recipientName: string;
  /** The recipient's email; empty means the buyer hands the code over themselves. */
  recipientEmail: string;
  /** A short message for the gift email, up to the server's limits.GIFT_MESSAGE_MAX; optional. */
  message: string;
  /** Send the gift email today, or on a chosen day. */
  when: 'today' | 'day';
  /** The chosen day, YYYY-MM-DD. */
  day: string;
  /** The gift email's language. */
  language: 'en' | 'es';
}

/** An email address, loosely, as the backend checks it. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * One labelled field.
 *
 * @param props.id - The input's id.
 * @param props.label - What it is.
 * @param props.hint - A line under it.
 * @param props.error - What is wrong with it.
 * @param props.children - The input.
 */
const Field: React.FC<{ id: string; label: string; hint?: string; error?: string | null; children: React.ReactNode }> = ({
  id, label, hint, error, children,
}) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-medium">{label}</label>
    {children}
    {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
  </div>
);

/**
 * The fields the form would refuse, before asking the server.
 *
 * @param form - As typed.
 * @param messageMax - The server's longest message (limits.GIFT_MESSAGE_MAX), or undefined before the app has heard it (the server then decides).
 * @returns The names of the fields that are wrong.
 */
function problemsOf(form: Form, messageMax: number | undefined): Set<keyof Form> {
  const problems = new Set<keyof Form>();
  if (!form.buyerName.trim()) problems.add('buyerName');
  if (!EMAIL_PATTERN.test(form.buyerEmail.trim())) problems.add('buyerEmail');
  if (form.recipientEmail.trim() && !EMAIL_PATTERN.test(form.recipientEmail.trim())) problems.add('recipientEmail');
  if (messageMax !== undefined && form.message.length > messageMax) problems.add('message');
  if (form.recipientEmail.trim() && form.when === 'day' && !DateTime.fromISO(form.day).isValid) problems.add('day');
  return problems;
}

/**
 * This browser's time zone (an IANA name), for the server to work out the
 * gift email's moment in; undefined when the browser does not say.
 *
 * @returns The zone, such as "America/Chicago".
 */
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The order the form makes: with a chosen day, that day and this browser's
 * time zone, from which the server works out the moment the gift email goes
 * (the Grow setting's hour on that day there).
 *
 * @param form - As typed (and checked).
 * @param buyerLanguage - The page's language: the receipt's.
 * @returns The order.
 */
function orderOf(form: Form, buyerLanguage: 'en' | 'es'): GiftOrder {
  const recipientEmail = form.recipientEmail.trim();
  const chosen = recipientEmail && form.when === 'day' ? DateTime.fromISO(form.day) : null;
  return {
    buyerName: form.buyerName.trim(),
    buyerEmail: form.buyerEmail.trim(),
    buyerLanguage,
    recipientName: form.recipientName.trim() || undefined,
    recipientEmail: recipientEmail || undefined,
    language: form.language,
    message: form.message.trim() || undefined,
    ...(chosen?.isValid ? { sendOn: chosen.toISODate() ?? undefined, timeZone: browserTimeZone() } : {}),
  };
}

/** The page at /gift. */
export const GiftPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const language = useGiftLanguage();
  const [params] = useSearchParams();
  const [shop, setShop] = React.useState<GiftShop | null>(null);
  // What reading the shop threw, when it failed (its words: lib/errorMessage); null otherwise.
  const [loadFailed, setLoadFailed] = React.useState<{ error: unknown } | null>(null);
  const [form, setForm] = React.useState<Form>(() => ({
    buyerName: '', buyerEmail: '', recipientName: '', recipientEmail: '', message: '',
    when: 'today', day: DateTime.now().plus({ days: 1 }).toISODate() ?? '', language,
  }));
  const [tried, setTried] = React.useState(false);
  const [going, setGoing] = React.useState(false);
  // Back from Stripe to this page as it was left: Pay usable again.
  usePageRestored(() => setGoing(false));
  const [failed, setFailed] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getGiftShop()
      .then((answer) => { if (!cancelled) setShop(answer); })
      .catch((error) => { if (!cancelled) setLoadFailed({ error }); });
    return () => { cancelled = true; };
  }, []);

  /**
   * Changes one field.
   *
   * @param key - Which.
   * @param value - Its new value.
   */
  const set = <K extends keyof Form>(key: K, value: Form[K]): void => setForm((now) => ({ ...now, [key]: value }));

  const messageMax = useGrowLimits().GIFT_MESSAGE_MAX;
  const problems = problemsOf(form, messageMax);
  /**
   * What is wrong with a field, once the form has been tried.
   *
   * @param key - The field.
   * @param words - What to say when it is wrong.
   */
  const errorOf = (key: keyof Form, words: string): string | null => (tried && problems.has(key) ? words : null);

  /** Checks the form, then leaves for Stripe Checkout. */
  const pay = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setTried(true);
    setFailed(null);
    if (problems.size > 0) return;
    setGoing(true);
    try {
      window.location.assign(await startGiftCheckout(orderOf(form, language)));
    } catch (error) {
      // Words the panel blocks (D15) are said plainly; anything else is the general failure.
      const problems: unknown = (error as { response?: { data?: { problems?: unknown } } })?.response?.data?.problems;
      const list = Array.isArray(problems) ? problems : [];
      if (list.includes('messageWords')) setFailed(t('gift.buy.messageWords'));
      else if (list.includes('nameWords')) setFailed(t('gift.buy.nameWords'));
      else setFailed(errorMessage(error, t('gift.buy.failed')));
      setGoing(false);
    }
  };

  const today = DateTime.now();
  const minDay = today.plus({ days: 1 }).toISODate() ?? '';
  const maxDay = today.plus({ days: shop?.sendMaxDays ?? 365 }).toISODate() ?? '';
  const price = shop?.price ? formatPrice(shop.price, language === 'es' ? 'es-US' : 'en-US') : null;
  const hasRecipientEmail = form.recipientEmail.trim().length > 0;

  return (
    <GiftShell>
      <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.buy.title')}</h1>
      <p className="mt-3 text-muted-foreground">{t('gift.buy.intro')}</p>

      {params.get('cancelled') && (
        <p className="mt-4 rounded-sm border bg-card px-3 py-2 text-sm text-muted-foreground">{t('gift.buy.cancelled')}</p>
      )}

      {!shop && !loadFailed && (
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      )}
      {loadFailed && (
        <p role="alert" className="mt-8 text-sm text-destructive">
          <FailureText text={errorMessage(loadFailed.error, t('gift.buy.loadFailed'))} />
        </p>
      )}

      {shop?.state === 'soon' && (
        <p className="mt-8 rounded-sm border bg-card px-4 py-3 text-lg">{t('gift.buy.soon', { date: DateTime.fromISO(shop.opensOn).setLocale(language).toLocaleString({ month: 'long', day: 'numeric' }) })}</p>
      )}
      {shop?.state === 'unavailable' && (
        <p className="mt-8 rounded-sm border bg-card px-4 py-3">{t('gift.buy.unavailable')}</p>
      )}

      {shop?.state === 'open' && (
        <form className="mt-8 space-y-6" onSubmit={(event) => { void pay(event); }} noValidate>
          {price && <p className="text-xl font-semibold">{t('gift.buy.price', { price })}</p>}

          <fieldset className="space-y-4">
            <legend className="mb-2 font-grow text-lg font-semibold">{t('gift.buy.fromHeading')}</legend>
            <Field id="gift-buyer-name" label={t('gift.buy.buyerName')} error={errorOf('buyerName', t('gift.buy.needName'))}>
              <Input id="gift-buyer-name" autoComplete="name" maxLength={80} value={form.buyerName} onChange={(e) => set('buyerName', e.target.value)} />
            </Field>
            <Field
              id="gift-buyer-email"
              label={t('gift.buy.buyerEmail')}
              hint={t('gift.buy.buyerEmailHint')}
              error={errorOf('buyerEmail', t('gift.buy.needEmail'))}
            >
              <Input id="gift-buyer-email" type="email" autoComplete="email" maxLength={254} value={form.buyerEmail} onChange={(e) => set('buyerEmail', e.target.value)} />
            </Field>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="mb-2 font-grow text-lg font-semibold">{t('gift.buy.toHeading')}</legend>
            <Field id="gift-recipient-name" label={t('gift.buy.recipientName')}>
              <Input id="gift-recipient-name" maxLength={80} value={form.recipientName} onChange={(e) => set('recipientName', e.target.value)} />
            </Field>
            <Field
              id="gift-recipient-email"
              label={t('gift.buy.recipientEmail')}
              hint={t('gift.buy.recipientEmailHint')}
              error={errorOf('recipientEmail', t('gift.buy.badEmail'))}
            >
              <Input id="gift-recipient-email" type="email" maxLength={254} value={form.recipientEmail} onChange={(e) => set('recipientEmail', e.target.value)} />
            </Field>
            <Field
              id="gift-message"
              label={t('gift.buy.message')}
              hint={messageMax === undefined ? undefined : t('gift.buy.messageHint', { left: Math.max(0, messageMax - form.message.length) })}
              error={messageMax === undefined ? null : errorOf('message', t('gift.buy.messageTooLong', { max: messageMax }))}
            >
              <textarea
                id="gift-message"
                rows={4}
                maxLength={messageMax}
                value={form.message}
                onChange={(e) => set('message', e.target.value)}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </Field>
          </fieldset>

          {hasRecipientEmail && (
            <fieldset className="space-y-3">
              <legend className="mb-2 font-grow text-lg font-semibold">{t('gift.buy.whenHeading')}</legend>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="gift-when" checked={form.when === 'today'} onChange={() => set('when', 'today')} />
                {t('gift.buy.whenToday')}
              </label>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input type="radio" name="gift-when" checked={form.when === 'day'} onChange={() => set('when', 'day')} />
                {t('gift.buy.whenDay')}
                <Input
                  type="date"
                  aria-label={t('gift.buy.whenDay')}
                  className="w-auto"
                  min={minDay}
                  max={maxDay}
                  value={form.day}
                  onChange={(e) => { set('day', e.target.value); set('when', 'day'); }}
                />
              </label>
              {/* The hour is the server's (gift.SEND_LOCAL_HOUR, the shop's sendHour). */}
              {form.when === 'day' && (
                <p className="text-xs text-muted-foreground">{t('gift.buy.whenHint', { hour: shop.sendHour })}</p>
              )}
              {errorOf('day', t('gift.buy.badDay')) && <p className="text-xs text-destructive" role="alert">{t('gift.buy.badDay')}</p>}
              <Field id="gift-language" label={t('gift.buy.language')}>
                <select
                  id="gift-language"
                  value={form.language}
                  onChange={(e) => set('language', e.target.value === 'es' ? 'es' : 'en')}
                  className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </Field>
            </fieldset>
          )}

          {failed && <p role="alert" className="text-sm text-destructive"><FailureText text={failed} /></p>}

          <div>
            <Button type="submit" className="w-full sm:w-auto" disabled={going}>
              {going ? <Spinner size={16} color="currentColor" /> : null}
              <span className={going ? 'ml-2' : ''}>{price ? t('gift.buy.pay', { price }) : t('gift.buy.payNoPrice')}</span>
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">{t('gift.buy.smallPrint')}</p>
          </div>
        </form>
      )}

      <ul className="mt-10 space-y-1 text-sm text-muted-foreground">
        <li>{t('gift.buy.notes.redeem')}</li>
        <li>{t('gift.buy.notes.renew')}</li>
        <li>
          {t('gift.buy.notes.have')}{' '}
          <a href="/gift/redeem" className="text-primary underline underline-offset-2">{t('gift.buy.notes.redeemLink')}</a>
        </li>
      </ul>
    </GiftShell>
  );
};

export default GiftPage;
